/** CrmNoteOverlay (Item E, slice E1) — the note view/editor overlay (spec §7.1).
 *
 * Opens over a profile or record via the `note` selection param (id, or 'new'). The NUX-1 side-sheet
 * adds explicit host context, list controls, governed contextual mentions, acknowledged Done/save,
 * local crash recovery and delete safety around the existing one-create/revision-CAS state machine.
 * All authorization and audience computation remain server-side. */
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import type { Editor } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/client';
import {
  useAddNoteLink, useCreateNote, useDeleteNote, useNote, useNoteLinkLookup,
  usePatchNote, useRemoveNoteLink,
} from '../../hooks/useCrm';
import type { CrmNote, CrmNoteVisibility } from '../../types';
import { fmtShortDate } from './crmFormat';
import styles from './crm.module.css';
import { NoteImage, uploadNoteImage } from './crmNoteImage';
import { createMentionExtension, type MentionContextTarget } from './crmNoteMention';

const AUTOSAVE_MS = 1200;
const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

// The entity a note is authored FROM — person/org profiles (E1) or a list_entry/opportunity record
// (D6). Seeds the auto-link + is a valid crm_note_link target_kind.
export type CrmNoteHostKind = 'person' | 'organization' | 'list_entry';

const VISIBILITY_LABEL: Record<CrmNoteVisibility, string> = {
  firm: 'Everyone with access', author_only: 'Only me', team: 'Team',
};

const MEANINGFUL_NODES = new Set(['mention', 'image', 'horizontalRule', 'codeBlock']);

/** Text is not the only content: a mention, image or rule must never be deleted as an "empty" note. */
export function docHasMeaningfulContent(doc: unknown): boolean {
  let meaningful = false;
  const walk = (n: unknown) => {
    if (meaningful) return;
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (!n || typeof n !== 'object') return;
    const node = n as { type?: unknown; text?: unknown; content?: unknown };
    if (typeof node.text === 'string' && node.text.trim() !== '') { meaningful = true; return; }
    if (typeof node.type === 'string' && MEANINGFUL_NODES.has(node.type)) { meaningful = true; return; }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(doc);
  return meaningful;
}

function docIsEmpty(doc: unknown): boolean { return !docHasMeaningfulContent(doc); }

interface RecoveryDraft { doc: unknown; visibility: CrmNoteVisibility; savedAt: number }

function loadRecoveryDraft(key: string): RecoveryDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RecoveryDraft;
    return parsed && parsed.doc ? parsed : null;
  } catch { return null; }
}

function writeRecoveryDraft(key: string, draft: RecoveryDraft | null): void {
  try {
    if (draft) window.localStorage.setItem(key, JSON.stringify(draft));
    else window.localStorage.removeItem(key);
  } catch { /* storage can be disabled; the server save path remains authoritative */ }
}

interface Props {
  /** The note param value: a numeric id (existing) or 'new'. */
  noteParam: string;
  /** The entity this note is authored from — seeds a link on a brand-new note. */
  hostKind: CrmNoteHostKind;
  hostId: number;
  /** Governed display label already present on the host surface. */
  hostLabel?: string | null;
  /** Governed records already visible on the host surface; ranking only, never authorization. */
  mentionContext?: MentionContextTarget[];
  onClose: () => void;
  /** Called with the fresh id when a new note is first created (so the URL param can advance). */
  onCreated: (id: number) => void;
}

export function CrmNoteOverlay({
  noteParam, hostKind, hostId, hostLabel, mentionContext = [], onClose, onCreated,
}: Props) {
  const isNew = noteParam === 'new';
  const noteId = isNew ? null : Number(noteParam);
  const noteQ = useNote(noteId);
  const create = useCreateNote();
  const patch = usePatchNote();
  const del = useDeleteNote();
  const addLink = useAddNoteLink();
  const removeLink = useRemoveNoteLink();

  const [visibility, setVisibility] = useState<CrmNoteVisibility>('firm');
  const [saveState, setSaveState] = useState<'idle' | 'unsaved' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [staleBanner, setStaleBanner] = useState(false);
  const [confirmFirm, setConfirmFirm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return window.localStorage.getItem('crm-note-composer-expanded') === '1'; }
    catch { return false; }
  });
  const [imageError, setImageError] = useState<string | null>(null);   // E2 image upload failures
  const [linkError, setLinkError] = useState<string | null>(null);
  // Links buffered before the note exists (pre-create) — rendered as chips + removable + deduped, then
  // superseded by the server's links once the note is created (useNote refetch).
  const [pendingLinks, setPendingLinks] = useState<{ target_kind: string; target_id: number; name: string | null }[]>([]);

  // Autosave state machine (finding 19): one in-flight create; the newest doc queues while a save is
  // pending and flushes once the previous settles; a body-empty new note is never created.
  const idRef = useRef<number | null>(noteId);          // the note id once known
  const revRef = useRef<number>(1);                      // latest revision for the optimistic lock
  const savingRef = useRef(false);                       // a create/patch is in flight
  // newest {doc, vis} awaiting flush — carry the visibility that was current when queued (not the
  // in-flight flush's stale closure vis), else a visibility change during a pending save is lost.
  const pendingRef = useRef<{ doc: unknown; vis: CrmNoteVisibility } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const closeWhenSavedRef = useRef(false);
  const recoveryKeyRef = useRef(`crm-note-draft:${hostKind}:${hostId}:${noteParam}`);
  const [recoveryDraft, setRecoveryDraft] = useState<RecoveryDraft | null>(
    () => loadRecoveryDraft(recoveryKeyRef.current));
  const linksRef = useRef<{ target_kind: string; target_id: number }[]>(
    isNew ? [{ target_kind: hostKind, target_id: hostId }] : []);
  // flush recurses to drain a queued save; route it through a ref so the useCallback doesn't
  // reference itself by name (no-use-before-define) while staying a single stable closure.
  const flushRef = useRef<(doc: unknown, vis: CrmNoteVisibility) => void>(() => {});
  // image paste/drop handler routed through a ref so the (create-once) editor config never captures a
  // stale closure — it always calls the latest handler (which reads the current note id).
  const insertImagesRef = useRef<(files: File[]) => void>(() => {});
  const imageInputRef = useRef<HTMLInputElement | null>(null);   // the hidden file input for the 🖼 button

  const note: CrmNote | undefined = noteQ.data;
  const canEdit = isNew || note?.can_edit === true;
  const canDelete = !isNew && note?.can_delete === true;

  // Keep idRef/revRef current from every note read (the optimistic lock must track the latest server
  // revision). Visibility hydrates ONCE (visHydratedRef) — re-setting it on every mutation-triggered
  // refetch would clobber a visibility change the user just made. Init `isNew` (mirroring loadedRef):
  // a created-in-session note never re-hydrates visibility from its own create snapshot (which could
  // revert a change made in the brief window before the create refetch lands); existing notes hydrate once.
  const visHydratedRef = useRef(isNew);
  useEffect(() => {
    if (note) {
      idRef.current = note.id;
      revRef.current = note.revision;
      if (!visHydratedRef.current) { visHydratedRef.current = true; setVisibility(note.visibility_mode); }
    }
  }, [note]);

  const mentionTargets: MentionContextTarget[] = [
    { kind: hostKind, id: hostId, name: hostLabel },
    ...mentionContext,
  ].filter((item, index, all) => all.findIndex(
    (candidate) => candidate.kind === item.kind && candidate.id === item.id) === index);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: 'Add context, an observation, or a reminder…' }),
      NoteImage,
      createMentionExtension(mentionTargets),
    ],
    content: EMPTY_DOC,
    editable: true,
    editorProps: {
      // Image paste/drop → upload to the governed asset proxy + insert. Routed through the ref so the
      // config (created once) never holds a stale note id.
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        insertImagesRef.current(files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        insertImagesRef.current(files);
        return true;
      },
      handleKeyDown: (_view, event) => {
        const current = editorRef.current;
        if (!current?.isActive('listItem') || event.key !== 'Tab') return false;
        event.preventDefault();
        return event.shiftKey
          ? current.chain().focus().liftListItem('listItem').run()
          : current.chain().focus().sinkListItem('listItem').run();
      },
    },
  });
  useEffect(() => { editorRef.current = editor; }, [editor]);
  useEffect(() => { editor?.setEditable(canEdit); }, [editor, canEdit]);

  // Upload image files to the current note + insert them. Blocks until the note exists (the asset route
  // needs a note id; a body-empty note isn't created yet) — mirrors the spec "editor blocks image
  // insertion until the note exists".
  const insertImages = useCallback((files: File[]) => {
    const nid = idRef.current;
    if (nid == null) { setImageError('Type something first — images can be added once the note is saved.'); return; }
    setImageError(null);
    files.forEach((file) => {
      uploadNoteImage(nid, file)
        .then((url) => editor?.chain().focus().setImage({ src: url }).run())
        .catch(() => setImageError('Image upload failed — check the file type (png/jpg/gif/webp, ≤5 MB).'));
    });
  }, [editor]);
  useEffect(() => { insertImagesRef.current = insertImages; }, [insertImages]);

  // Load an EXISTING note's body into the editor once. Init `isNew`: a note CREATED in this session
  // starts "already loaded" (loadedRef=true) so it NEVER re-hydrates from the server snapshot when
  // useNote fetches the just-created note — that would clobber text typed after the create + jump the
  // cursor (data loss on the create→hydrate boundary). A REOPENED existing note (isNew=false) starts
  // not-loaded and hydrates its body exactly once.
  const loadedRef = useRef(isNew);
  useEffect(() => {
    if (editor && note && !loadedRef.current) {
      loadedRef.current = true;
      editor.commands.setContent((note.body_json as object) ?? EMPTY_DOC);
    }
  }, [editor, note]);

  // Put the user where the work is. Existing notes wait for hydration so focus never lands in an
  // empty pre-load document and then jumps when the body arrives.
  useEffect(() => {
    if (!editor || !canEdit) return;
    if (isNew || note) requestAnimationFrame(() => editor.commands.focus('end'));
  }, [editor, canEdit, isNew, note]);

  const clearRecovery = useCallback(() => {
    writeRecoveryDraft(recoveryKeyRef.current, null);
    setRecoveryDraft(null);
  }, []);

  const persistRecovery = useCallback((doc: unknown, vis: CrmNoteVisibility) => {
    const draft = { doc, visibility: vis, savedAt: Date.now() };
    writeRecoveryDraft(recoveryKeyRef.current, draft);
  }, []);

  const flush = useCallback((doc: unknown, vis: CrmNoteVisibility) => {
    if (savingRef.current) { pendingRef.current = { doc, vis }; return; }
    if (docIsEmpty(doc)) {
      if (idRef.current == null) {
        setSaveState('idle');
        clearRecovery();
        if (closeWhenSavedRef.current) { closeWhenSavedRef.current = false; onClose(); }
      } else {
        closeWhenSavedRef.current = false;
        setSaveState('error');
        setSaveError('A saved note can’t be empty. Restore text or use Delete note.');
      }
      return;
    }
    savingRef.current = true;
    setSaveState('saving');
    setSaveError(null);
    const settle = (ok: boolean) => {
      savingRef.current = false;
      if (!ok) {
        closeWhenSavedRef.current = false;
        setSaveState('error');
        setSaveError('Not saved. Your draft is retained on this device.');
        return;
      }
      const queued = pendingRef.current;
      pendingRef.current = null;
      if (queued) {
        flushRef.current(queued.doc, queued.vis);
        return;
      }
      setSaveState('saved');
      if (JSON.stringify(editorRef.current?.getJSON() ?? EMPTY_DOC) === JSON.stringify(doc)) {
        clearRecovery();
      }
      if (closeWhenSavedRef.current) {
        closeWhenSavedRef.current = false;
        onClose();
      }
    };
    if (idRef.current == null) {
      create.mutate(
        { body_json: doc, visibility_mode: vis, links: linksRef.current },
        {
          onSuccess: (n) => { idRef.current = n.id; revRef.current = n.revision; onCreated(n.id); settle(true); },
          onError: () => settle(false),
        });
    } else {
      patch.mutate(
        { noteId: idRef.current, expected_revision: revRef.current, body_json: doc, visibility_mode: vis },
        {
          onSuccess: (r) => {
            if (r && 'revision' in r) revRef.current = (r as CrmNote).revision;
            settle(true);
          },
          onError: (e) => {
            if (e instanceof ApiError && e.status === 409) setStaleBanner(true);
            settle(false);
          },
        });
    }
  }, [create, patch, onCreated, clearRecovery, onClose]);
  useEffect(() => { flushRef.current = flush; }, [flush]);

  // Debounced autosave on editor changes.
  useEffect(() => {
    if (!editor) return;
    const onUpdate = () => {
      if (!canEdit) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      const doc = editor.getJSON();
      setSaveState('unsaved');
      setSaveError(null);
      persistRecovery(doc, visibility);
      // Never translate clearing an existing note into an implicit delete. Done explains the remedy.
      if (idRef.current != null && docIsEmpty(doc)) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flush(editor.getJSON(), visibility);
      }, AUTOSAVE_MS);
    };
    editor.on('update', onUpdate);
    return () => { editor.off('update', onUpdate); };
  }, [editor, flush, visibility, canEdit, persistRecovery]);

  const done = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const doc = editor?.getJSON() ?? EMPTY_DOC;
    if (!canEdit) { onClose(); return; }
    closeWhenSavedRef.current = true;
    flush(doc, visibility);
  }, [editor, flush, visibility, canEdit, onClose]);

  // Clear any pending debounce timer on unmount — the Delete button and the profile-change close path
  // (CrmNotesSection drops the note param → this unmounts) both bypass closeWithFlush, so without this
  // a queued timer would fire AFTER teardown and create a ghost note on the old host (§7.1 "no ghost").
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleDelete = () => {
    if (timerRef.current) clearTimeout(timerRef.current);   // don't let a queued save race the delete
    if (idRef.current != null) {
      del.mutate(idRef.current, {
        onSuccess: () => { clearRecovery(); onClose(); },
        onError: () => {
          setConfirmDelete(false);
          setSaveState('error');
          setSaveError('Could not delete this note. Try again.');
        },
      });
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault(); done();
      } else if (event.key === 'Escape') {
        event.preventDefault(); done();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [done]);

  const toggleExpanded = () => {
    setExpanded((value) => {
      const next = !value;
      try { window.localStorage.setItem('crm-note-composer-expanded', next ? '1' : '0'); }
      catch { /* preference storage is best-effort */ }
      return next;
    });
  };

  const restoreRecovery = () => {
    if (!recoveryDraft || !editor) return;
    setVisibility(recoveryDraft.visibility);
    editor.commands.setContent(recoveryDraft.doc as object);
    setRecoveryDraft(null);
    editor.commands.focus('end');
  };

  const changeVisibility = (v: CrmNoteVisibility) => {
    if (!canEdit || v === 'team') return;
    // Firm-on-sensitive confirmation (finding 3): reaching for firm on a note whose ceiling is above
    // firm_wide surfaces the effective audience before committing.
    if (v === 'firm' && note && note.permission_class !== 'firm_wide' && !confirmFirm) {
      setConfirmFirm(true);
      return;
    }
    setVisibility(v);
    setConfirmFirm(false);
    flush(editor?.getJSON() ?? EMPTY_DOC, v);
  };

  // Link chips: from the server note once it exists, else the pre-create buffer (so a linked entity
  // shows a chip + is removable + deduped even before the note is saved). Keys normalize
  // opportunity→list_entry so dedup matches the server's link kinds.
  const chips: { key: string; label: string; onRemove?: () => void }[] = note
    ? note.links.map((l) => ({
        key: `l${l.link_id}`,
        label: (l.source === 'mention' ? '@' : '') + (l.name ?? '(restricted)'),
        onRemove: (l.target_kind === hostKind && l.target_id === hostId) ? undefined : () => {
          if (idRef.current == null) return;
          setLinkError(null);
          removeLink.mutate({ noteId: idRef.current, linkId: l.link_id }, {
            onError: () => setLinkError('Could not remove that related record.'),
          });
        },
      }))
    : [{
        key: 'host',
        label: `On: ${hostLabel || `${hostKind.replace('_', ' ')} #${hostId}`}`,
      }, ...pendingLinks.map((l, i) => ({
        key: `p${i}`,
        label: l.name ?? l.target_kind,
        // remove from BOTH the display buffer AND linksRef (the create payload) so a removed
        // pre-create link doesn't resurrect on create. Both hold the NORMALIZED target_kind, so
        // (target_kind, target_id) matches.
        onRemove: () => {
          setPendingLinks((p) => p.filter((_, j) => j !== i));
          linksRef.current = linksRef.current.filter(
            (x) => !(x.target_kind === l.target_kind && x.target_id === l.target_id));
        },
      }))];
  const linkedKeys = new Set([
    `${hostKind}:${hostId}`,
    ...(
    note ? note.links.map((l) => `${l.target_kind}:${l.target_id}`)
         : pendingLinks.map((l) => `${l.target_kind}:${l.target_id}`)
    ),
  ]);
  const addLinkFromResult = (r: { kind: string; id: number; name: string | null }) => {
    const tk = r.kind === 'opportunity' ? 'list_entry' : r.kind;   // normalized (opportunity→list_entry)
    if (idRef.current == null) {
      // store the NORMALIZED kind in BOTH buffers so pre-create removal can match; the backend
      // accepts target_kind='list_entry' directly (it normalizes 'opportunity' the same way).
      linksRef.current = [...linksRef.current, { target_kind: tk, target_id: r.id }];
      setPendingLinks((p) => [...p, { target_kind: tk, target_id: r.id, name: r.name }]);
    } else {
      setLinkError(null);
      addLink.mutate({ noteId: idRef.current, target_kind: r.kind, target_id: r.id }, {
        onError: () => setLinkError('Could not add that related record.'),
      });
    }
  };

  const notFound = !isNew && noteQ.isError;

  return (
    <div className={styles.noteOverlayBackdrop} role="dialog" aria-modal="true" aria-label="Note">
      <div className={`${styles.noteOverlay} ${expanded ? styles.noteOverlayExpanded : ''}`}>
        <div className={styles.noteOverlayHead}>
          <div className={styles.noteHeadText}>
            <div className={styles.noteTitle}>{isNew ? 'New note' : 'Note'}</div>
            <div className={styles.noteMeta}>
              {/* author === null == a SYSTEM note published from an auto-accepted AI block (S5c) */}
              {note?.author?.name ? `${note.author.name} · ` : note?.author === null ? 'AI · ' : ''}
              {note?.updated_at ? `edited ${fmtShortDate(note.updated_at)}` : 'Not saved yet'}
              {saveState === 'unsaved' && <span className={styles.noteUnsavedHint}> · Unsaved</span>}
              {saveState === 'saving' && <span className={styles.noteSaveHint}> · Saving…</span>}
              {saveState === 'saved' && <span className={styles.noteSaveHint}> · Saved</span>}
              {saveState === 'error' && <span className={styles.noteSaveErrorHint}> · Not saved</span>}
            </div>
            <div className={styles.notePrimaryContext}>
              <span>On:</span> {hostLabel || `${hostKind.replace('_', ' ')} #${hostId}`}
            </div>
          </div>
          <div className={styles.noteHeadActions}>
            <button type="button" className={styles.noteExpandBtn} onClick={toggleExpanded}
              aria-pressed={expanded}>{expanded ? 'Restore size' : 'Expand'}</button>
            {canDelete && (
              <button type="button" className={styles.noteDeleteBtn} onClick={() => setConfirmDelete(true)}>
                Delete note
              </button>
            )}
            <button type="button" className={styles.noteDoneBtn} onClick={done}
              disabled={saveState === 'saving'}>Done</button>
            <button type="button" className={styles.noteCloseBtn} onClick={done} aria-label="Close note">✕</button>
          </div>
        </div>

        {notFound ? (
          <div className={styles.noteNotFound}>This note isn’t available.</div>
        ) : (
          <div className={styles.noteComposerBody}>
            {recoveryDraft && canEdit && (
              <div className={styles.noteRecoveryBanner} role="status">
                <span>An unsaved draft from this device is available.</span>
                <button type="button" onClick={restoreRecovery}>Restore draft</button>
                <button type="button" onClick={clearRecovery}>Discard</button>
              </div>
            )}
            {staleBanner && (
              <div className={styles.noteStaleBanner} role="alert">
                This note changed elsewhere — reopen to see the latest.
              </div>
            )}
            {saveError && (
              <div className={styles.noteSaveError} role="alert">
                <span>{saveError}</span>
                {canEdit && !docIsEmpty(editor?.getJSON() ?? EMPTY_DOC) && (
                  <button type="button" onClick={() => flush(editor?.getJSON() ?? EMPTY_DOC, visibility)}>
                    Retry
                  </button>
                )}
              </div>
            )}
            {confirmDelete && (
              <div className={styles.noteDeleteConfirm} role="alertdialog" aria-label="Delete note?">
                <span>Delete this note? This removes it from every linked record.</span>
                <button type="button" className={styles.noteDeleteConfirmBtn}
                  disabled={del.isPending} onClick={handleDelete}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            )}

            <div className={styles.noteVisibilityRow}>
              <span className={styles.noteFieldLabel}>Audience</span>
              {(['firm', 'author_only'] as CrmNoteVisibility[]).map((v) => (
                <button key={v} type="button" disabled={!canEdit}
                  aria-pressed={visibility === v}
                  className={`${styles.noteVisChip} ${visibility === v ? styles.noteVisChipActive : ''}`}
                  title={v === 'firm' ? 'Visible to colleagues who can access every linked record' : undefined}
                  onClick={() => changeVisibility(v)}>
                  {VISIBILITY_LABEL[v]}
                </button>
              ))}
            </div>
            {confirmFirm && (
              <div className={styles.noteConfirmFirm} role="alert">
                This note is linked to sensitive material ({note?.permission_class}). Making it
                available to colleagues shares it with everyone in that access class.{' '}
                <button type="button" className={styles.noteConfirmBtn}
                  onClick={() => changeVisibility('firm')}>Share with everyone who has access</button>
                <button type="button" className={styles.noteConfirmCancel}
                  onClick={() => setConfirmFirm(false)}>Cancel</button>
              </div>
            )}

            <LinkChips chips={chips} linkedKeys={linkedKeys} onAdd={addLinkFromResult}
              canEdit={canEdit} />
            {linkError && <div className={styles.noteImageErrorLine} role="alert">{linkError}</div>}

            {canEdit && <div className={styles.noteToolbar}>
              <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()}
                className={editor?.isActive('bold') ? styles.noteTbActive : ''} aria-label="Bold"><b>B</b></button>
              <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={editor?.isActive('italic') ? styles.noteTbActive : ''} aria-label="Italic"><i>I</i></button>
              <button type="button" onClick={() => editor?.chain().focus().toggleUnderline().run()}
                className={editor?.isActive('underline') ? styles.noteTbActive : ''} aria-label="Underline"><u>U</u></button>
              <button type="button" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                className={editor?.isActive('heading', { level: 2 }) ? styles.noteTbActive : ''} aria-label="Heading 2">H2</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                className={editor?.isActive('heading', { level: 3 }) ? styles.noteTbActive : ''} aria-label="Heading 3">H3</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={editor?.isActive('bulletList') ? styles.noteTbActive : ''} aria-label="Bulleted list">• List</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={editor?.isActive('orderedList') ? styles.noteTbActive : ''} aria-label="Numbered list">1. List</button>
              <button type="button" disabled={!editor?.can().sinkListItem('listItem')}
                onClick={() => editor?.chain().focus().sinkListItem('listItem').run()}
                aria-label="Indent list item" title="Indent list item (Tab)">→</button>
              <button type="button" disabled={!editor?.can().liftListItem('listItem')}
                onClick={() => editor?.chain().focus().liftListItem('listItem').run()}
                aria-label="Outdent list item" title="Outdent list item (Shift+Tab)">←</button>
              <button type="button" aria-label="Link"
                className={editor?.isActive('link') ? styles.noteTbActive : ''}
                onClick={() => {
                  if (editor?.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
                  const url = window.prompt('Link URL');
                  if (url) editor?.chain().focus().setLink({ href: url }).run();
                }}>🔗</button>
              <button type="button" aria-label="Insert image" onClick={() => imageInputRef.current?.click()}>🖼</button>
              <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                multiple hidden onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) insertImages(files);
                  e.target.value = '';
                }} />
            </div>}
            {imageError && <div className={styles.noteImageErrorLine} role="alert">{imageError}</div>}
            {!canEdit && <div className={styles.noteReadOnly}>Read-only note</div>}
            <EditorContent editor={editor} className={styles.noteEditor} />
          </div>
        )}
      </div>
    </div>
  );
}

// Linked-entity chip row + adder (§7.2). Chips are a normalized display model (server links once the
// note exists, else the pre-create buffer); mention-sourced links show an @. The adder composes the
// governed lookup (person/org/opportunity — the same lookup as the ref-cell editor) and dedups against
// already-linked keys (opportunity normalized to list_entry).
function LinkChips({ chips, linkedKeys, onAdd, canEdit }: {
  chips: { key: string; label: string; onRemove?: () => void }[];
  linkedKeys: Set<string>;
  onAdd: (r: { kind: string; id: number; name: string | null }) => void;
  canEdit: boolean;
}) {
  const [kind, setKind] = useState<'person' | 'organization' | 'opportunity'>('person');
  const [q, setQ] = useState('');
  const lookup = useNoteLinkLookup(kind, q);

  return (
    <div className={styles.noteLinks}>
      <div className={styles.noteChipRow}>
        {chips.map((c) => (
          <span key={c.key} className={styles.noteChip}>
            {c.label}
            {canEdit && c.onRemove && (
              <button type="button" className={styles.noteChipX} aria-label={`Remove link ${c.label}`}
                onClick={c.onRemove}>✕</button>
            )}
          </span>
        ))}
      </div>
      {canEdit && <div className={styles.noteAdder}>
        <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} aria-label="Link type">
          <option value="person">Person</option>
          <option value="organization">Organization</option>
          <option value="opportunity">Opportunity</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Link an entity…"
          aria-label="Search entities to link" />
        {q.trim().length >= 2 && (lookup.data?.results?.length ?? 0) > 0 && (
          <ul className={styles.noteAdderResults}>
            {lookup.data!.results
              .filter((r) => !linkedKeys.has(`${r.kind === 'opportunity' ? 'list_entry' : r.kind}:${r.id}`))
              .map((r) => (
                <li key={`${r.kind}:${r.id}`}>
                  <button type="button" onClick={() => { onAdd({ kind: r.kind, id: r.id, name: r.name }); setQ(''); }}>
                    {r.name}{r.detail ? <span className={styles.noteAdderDetail}> · {r.detail}</span> : null}
                  </button>
                </li>
              ))}
          </ul>
        )}
        {q.trim().length >= 2 && lookup.isLoading && (
          <div className={styles.noteAdderStatus}>Searching…</div>
        )}
        {q.trim().length >= 2 && lookup.isError && (
          <div className={styles.noteAdderStatus} role="alert">Search failed — try again.</div>
        )}
        {q.trim().length >= 2 && !lookup.isLoading && !lookup.isError
          && (lookup.data?.results?.length ?? 0) === 0 && (
            <div className={styles.noteAdderStatus}>No matching records.</div>
          )}
      </div>}
    </div>
  );
}
